import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  IBasicOptionSummary,
  IBenefitSummary,
  IPlanOptionsSummary,
  IPlanSummary,
} from '@nibtravel/shared/util/models';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CancellationHelper } from '../helpers/cancellation.helper';

import {
  BasicOptionFn,
  defaultPlanState,
  IPlanState,
  PlanSummaryMap,
} from '../model/plans-state-model';
import { PlanDataService } from '../plan.data-service';
import { PlansAction } from './plans.action';
import { PlanBenefitsHelper } from '../helpers/benefits.helper';

@State<IPlanState>({
  name: 'plans',
  defaults: defaultPlanState,
})
@Injectable()
export class PlansState {
  constructor(
    private plansService: PlanDataService,
    private cancellationHelper: CancellationHelper
  ) {}

  @Selector()
  public static getState(state: IPlanState): IPlanState {
    return state;
  }

  @Selector()
  public static getPlanSummary(
    state: IPlanState,
    planId: number
  ): IPlanSummary | undefined {
    return state.planSummaries && state.planSummaries[planId];
  }

  @Selector()
  public static getPlanBenefitsSummary(
    state: IPlanState
  ): IBenefitSummary[] | undefined {
    return state.planBenefitsSummary;
  }
  /**
   *
   * @description return the currency for the plan
   * @param state
   */
  @Selector()
  public static getCurrency(state: IPlanState) {
    return (planId: number) => {
      return state.planSummaries?.[planId]?.currencyCode ?? 'AUD';
    };
  }

  /**
   *
   * @param state
   * @description returns plan based on planId
   */
  @Selector()
  public static getPlan(
    state: IPlanState
  ): (planId: number) => IPlanSummary | undefined {
    return (planId: number): IPlanSummary | undefined => {
      return state.planSummaries && state.planSummaries[planId];
    };
  }

  @Selector()
  public static pdsLink(state: IPlanState, planId: number): string | undefined {
    return state.planSummaries?.[planId]?.pdsUrl;
  }

  @Selector()
  public static getPlanSummaries(state: IPlanState): PlanSummaryMap {
    return state.planSummaries;
  }

  @Selector()
  public static currencyCode(state: IPlanState): (planId: number) => string {
    return (planId: number) => {
      const plan = state.planSummaries ? state.planSummaries[planId] : null;
      return plan?.currencyCode ?? '';
    };
  }

  @Selector()
  public static planOptionsSummary(
    state: IPlanState
  ): Record<number, IPlanOptionsSummary> | undefined {
    return state.planOptionsSummary;
  }

  @Selector()
  public static basicOptions(state: IPlanState): BasicOptionFn {
    return (planId: number | undefined): IBasicOptionSummary[] | undefined => {
      if (planId && state.planOptionsSummary?.[planId]) {
        return (
          state.planOptionsSummary &&
          state.planOptionsSummary[planId].basicOptions
        );
      }
      return undefined;
    };
  }

  @Action(PlansAction.LoadPlansData)
  loadPlanData(
    ctx: StateContext<IPlanState>,
    { brand, planIds }: PlansAction.LoadPlansData
  ): Observable<unknown> {
    const { planSummaries } = ctx.getState();
    const fetchPlanIds = planIds.filter((planId) => !planSummaries?.[planId]);

    return fetchPlanIds?.length > 0
      ? forkJoin({
          plans: this.plansService.loadPlansData(brand, fetchPlanIds),
          benefits: this.plansService.loadPlanBenefitsData(brand, fetchPlanIds),
        }).pipe(
          tap((data) => {
            ctx.setState(
              patch({
                planSummaries: patch(data.plans),
                planBenefitsSummaryLists: patch(data.benefits),
              })
            );

            return ctx.dispatch(new PlansAction.CalculatePlanBenefitsSummary());
          })
        )
      : EMPTY;
  }

  @Action(PlansAction.LoadOptionsSummary)
  loadOptions(
    ctx: StateContext<IPlanState>,
    { planId, brand }: PlansAction.LoadOptionsSummary
  ): Observable<Record<number, IPlanOptionsSummary>> {
    const state = ctx.getState();
    const planOptions = state.planOptionsSummary
      ? state.planOptionsSummary[planId]
      : undefined;

    return planOptions
      ? of({ [planId]: planOptions })
      : this.plansService.loadPlanOptions(planId, brand).pipe(
          tap((options) =>
            ctx.setState(
              patch({
                planOptionsSummary: options,
                apiPlanOptions: options,
              })
            )
          )
        );
  }

  @Action(PlansAction.CalculatePlanBenefitsSummary)
  calculatePlanBenefitsSummary(ctx: StateContext<IPlanState>): void {
    const { planBenefitsSummaryLists } = ctx.getState();

    const planBenefitsSummary: IBenefitSummary[] =
      PlanBenefitsHelper.toBenefitsSummary(planBenefitsSummaryLists);

    ctx.setState(patch({ planBenefitsSummary }));
  }

  @Action(PlansAction.CalculateImplicitCancellationValue)
  calculateImplicitCancellationValue(
    ctx: StateContext<IPlanState>,
    { planId, policyHolders }: PlansAction.CalculateImplicitCancellationValue
  ) {
    const state = ctx.getState();
    const next = this.cancellationHelper.setConfigWithDefaultImplict(
      state,
      planId,
      policyHolders
    );
    ctx.setState(
      patch({
        planOptionsSummary: patch({ [planId]: next.optionSummary }),
        implicitCancellationValue: next.implicitCancellationValue,
      })
    );
    return next.implicitCancellationValue;
  }
}
